'use client'

import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import { Icon } from '@iconify/react'
import CustomAvatar from '../../../../components/common/Template_Layouts/@core/components/mui/Avatar'

const ExportGrid = ({ data = [], loading }) => {
    const [selectedId, setSelectedId] = useState(null)

    useEffect(() => {
        if (!selectedId && data.length > 0) {
            setSelectedId(data[0].ExportName + data[0].CreatedDate)
        }
    }, [data, selectedId])

    if (loading && data.length === 0) return <Box sx={{ p: 10, textAlign: 'center' }}>Loading export history...</Box>
    if (!loading && data.length === 0) return <Box sx={{ p: 10, textAlign: 'center' }}>No export history found for the selected date range.</Box>

    const selectedItem = data.find(item => (item.ExportName + item.CreatedDate) === selectedId) || data[0]

    const downloadExcel = (relativePath) => {
        if (relativePath) {
            const safeUrl = relativePath.replace(/([^:]\/)\/+/g, "$1").replace(/\\/g, "/");
            const link = document.createElement('a');
            link.href = safeUrl;
            link.download = safeUrl.split('/').pop();
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            alert("File not available.");
        }
    };

    return (
        <Box sx={{ display: 'flex', width: '100%', height: 'calc(100vh - 350px)', minHeight: '600px', gap: 4 }}>
            {/* Left Sidebar - List of Exports */}
            <Box sx={{
                width: '350px',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                overflowY: 'auto',
                pr: 2,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '10px' }
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, px: 2 }}>Export History</Typography>
                {data.map((item, idx) => {
                    const id = item.ExportName + item.CreatedDate;
                    const status = (item.Status || '').toLowerCase();
                    const isSelected = selectedId === id;

                    return (
                        <Card
                            key={idx}
                            onClick={() => setSelectedId(id)}
                            sx={{
                                cursor: 'pointer',
                                border: isSelected ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                                transition: 'all 0.2s ease',
                                '&:hover': { boxShadow: 2 }
                            }}
                        >
                            <CardContent sx={{ p: '12px !important' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                                    <CustomAvatar skin='light' color='success' variant='rounded' size={32}>
                                        <Icon icon="ri:file-excel-2-line" fontSize={18} />
                                    </CustomAvatar>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.ExportName || 'Unnamed Export'}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{item.CreatedDate}</Typography>
                                    <Chip
                                        label={item.Status}
                                        size="small"
                                        variant="tonal"
                                        color={status === 'processed' ? 'success' : status === 'failed' ? 'error' : 'warning'}
                                        sx={{ height: '20px', fontSize: '10px' }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    )
                })}
            </Box>

            {/* Right Pane - Detailed View */}
            <Box sx={{ flex: 1, backgroundColor: '#ffffff', borderRadius: 1, border: '1px solid #e2e8f0', overflowY: 'auto' }}>
                {selectedItem ? (
                    <Box sx={{ p: 8 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <CustomAvatar skin='light' color='success' variant='rounded' size={56}>
                                    <Icon icon="ri:file-excel-fill" fontSize={32} />
                                </CustomAvatar>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{selectedItem.ExportName}</Typography>
                                    <Typography variant="body2" color="textSecondary">Created on {selectedItem.CreatedDate}</Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 8 }} />

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 6, mb: 8 }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 1 }}>File Name</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedItem.ExportedFileName || 'N/A'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 1 }}>Total Records</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{selectedItem.TotalRecordsExported || '0'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 1 }}>Current Status</Typography>
                                <Chip
                                    label={selectedItem.Status}
                                    color={selectedItem.Status?.toLowerCase() === 'processed' ? 'success' : 'warning'}
                                    variant="tonal"
                                />
                            </Box>
                        </Box>

                        <Box sx={{ p: 6, backgroundColor: '#f8fafc', borderRadius: 2, border: '1px dashed #cbd5e1', textAlign: 'center' }}>
                            <Icon icon="ri:information-line" fontSize={24} style={{ color: '#64748b', marginBottom: '8px' }} />
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
                                Your export is ready for download. This file contains all requested lead data processed on {selectedItem.CreatedDate}.
                            </Typography>
                            <Button
                                variant="contained"
                                color="success"
                                size="large"
                                startIcon={<Icon icon="ri:download-2-fill" />}
                                onClick={() => downloadExcel(selectedItem.ExportedFilePath)}
                                disabled={!selectedItem.ExportedFilePath || selectedItem.Status?.toLowerCase() === 'unprocessed'}
                                sx={{ px: 10, py: 3, borderRadius: '8px', textTransform: 'none', fontSize: '16px', fontWeight: 600 }}
                            >
                                Download Excel File
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography color="textSecondary">Select an export record to view details</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default ExportGrid
